
import React from 'react';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { MoreHorizontal, Copy, Edit, Trash, Star, FileText, Image } from 'lucide-react';
import { CertificateTemplate } from '@/types/certificateTemplate';

interface CertificateTemplateCardProps {
  template: CertificateTemplate;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onSetDefault: () => void;
}

const CertificateTemplateCard: React.FC<CertificateTemplateCardProps> = ({
  template,
  onEdit,
  onDelete,
  onDuplicate,
  onSetDefault,
}) => {
  return (
    <Card className="overflow-hidden transition-all hover:shadow-md">
      <div className="relative">
        {template.metadata?.isPdf ? (
          <div className="h-40 bg-gray-100 flex items-center justify-center">
            <FileText className="h-16 w-16 text-gray-400" />
            <span className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded">
              PDF
            </span>
          </div>
        ) : template.thumbnail ? (
          <div className="h-40 bg-gray-100">
            <img
              src={template.thumbnail}
              alt={template.name}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="h-40 bg-gray-100 flex items-center justify-center">
            <Image className="h-16 w-16 text-gray-400" />
          </div>
        )}
        
        {template.isDefault && (
          <Badge className="absolute top-2 left-2 bg-primary">Default</Badge>
        )}
      </div>
      
      <CardContent className="p-4">
        <h3 className="font-semibold text-lg truncate">{template.name}</h3>
        {template.description && (
          <p className="text-muted-foreground text-sm mt-1 line-clamp-2">
            {template.description}
          </p>
        )}
      </CardContent>
      
      <CardFooter className="p-4 pt-0 flex justify-between">
        <Button
          variant="outline"
          size="sm"
          onClick={onEdit}
        >
          <Edit className="h-4 w-4 mr-1" />
          Edit
        </Button>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Template
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDuplicate}>
              <Copy className="h-4 w-4 mr-2" />
              Duplicate
            </DropdownMenuItem>
            {!template.isDefault && (
              <DropdownMenuItem onClick={onSetDefault}>
                <Star className="h-4 w-4 mr-2" />
                Set as Default
              </DropdownMenuItem>
            )}
            <DropdownMenuItem 
              onClick={onDelete}
              className="text-red-600"
              disabled={template.isDefault}
            >
              <Trash className="h-4 w-4 mr-2" />
              Delete Template
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardFooter>
    </Card>
  );
};

export default CertificateTemplateCard;
